import { muiGlyph } from "@/components/ui/mui-glyph";

// MUI's `Alert` glyphs — the four `defaultIconMapping` severity icons
// (`Alert.js:139-150`) and the close ✕ beside them (`Alert.js:239-250`),
// ported as shared Atlas components.
//
// ## Why these are hand-drawn rather than lucide lookalikes
//
// `MuiAlert` first shipped with lucide's nearest neighbours — `CircleAlert`,
// `CircleCheck`, `TriangleAlert` — which read as the same idea at a glance and
// are a different drawing at any size that matters. MUI's `ErrorOutline` is a
// hairline ring around a bar-and-dot; lucide's `CircleAlert` is a heavier
// stroked ring. MUI's `SuccessOutlined` is a broken ring whose tick *escapes*
// the circle at the top right; lucide's `CircleCheck` is a closed ring with
// the tick wholly inside. The donor draws the first of each pair, and once the
// toast needed them too (#409, which names `ErrorOutline` in its acceptance
// criteria), keeping lucide in the banner would have meant Atlas drawing two
// different glyphs for one donor glyph. So they port once, here, for both.
//
// The paths are the installed `@mui/material@9.2.0`'s own, read out of
// `node_modules/@mui/material/internal/svg-icons/` in the pinned modsy
// checkout — and the close ✕ out of `@mui/icons-material/Close.js`, which is
// where `Alert` takes it from — rather than from memory of what the icon set
// contains.
//
// ## Sizing follows the slot, as MUI's does
//
// `Alert` renders each of these with `fontSize="inherit"` and puts the actual
// size on the icon *slot* (`fontSize: 22`). `SvgIcon` is `width/height: 1em`,
// so the glyph takes that 22px from its parent. These keep that arrangement —
// `h-[1em] w-[1em]` here, `text-[22px]` on the slot — rather than hard-coding
// 22px, so the one number lives at the one place MUI puts it.

// MUI sizes these from the parent: `AlertIcon` sets `fontSize: 22` on the slot
// and the glyph is `width/height: 1em` (`Alert.js:105-112`, `SvgIcon.js:44-45`).
// Keeping `1em` here means the 22 lives at the one place MUI puts it — the
// slot — rather than being restated on every glyph.
const ALERT_GLYPH_BOX = "h-[1em] w-[1em]";

/** `@mui/material/internal/svg-icons/ErrorOutline`. */
export const MuiErrorOutlineIcon = muiGlyph(
  "mui-error-outline",
  "M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z",
  ALERT_GLYPH_BOX,
);

/** `@mui/material/internal/svg-icons/SuccessOutlined` — the tick that breaks
 * out of the ring, which is what distinguishes it from every closed-circle
 * check mark. */
export const MuiSuccessOutlinedIcon = muiGlyph(
  "mui-success-outlined",
  "M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2, 4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0, 0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z",
  ALERT_GLYPH_BOX,
);

/** `@mui/material/internal/svg-icons/ReportProblemOutlined`. */
export const MuiReportProblemOutlinedIcon = muiGlyph(
  "mui-report-problem-outlined",
  "M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z",
  ALERT_GLYPH_BOX,
);

/** `@mui/material/internal/svg-icons/InfoOutlined`. */
export const MuiInfoOutlinedIcon = muiGlyph(
  "mui-info-outlined",
  "M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20, 12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10, 10 0 0,0 12,2M11,17H13V11H11V17Z",
  ALERT_GLYPH_BOX,
);

/**
 * `@mui/icons-material/Close`, which `Alert` renders as its close button's
 * glyph.
 *
 * The one glyph here whose size is **not** the slot's: `Alert` passes
 * `fontSize="small"` explicitly (`Alert.js:248-249`), which is `SvgIcon`'s
 * `pxToRem(20)` and overrides the `IconButton size="small"`'s own 18px. So the
 * call site states 20px rather than leaving it to inherit.
 */
export const MuiCloseIcon = muiGlyph(
  "mui-close",
  "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  ALERT_GLYPH_BOX,
);

// The batch push's status glyphs (#1088), which the donor's stepper imports
// from `@mui/icons-material` (`PublishVersionNonLinearStepper.tsx:7-9`) — the
// *Outlined* themes of `CheckCircle` and `Error`, not the `…Outline` icons,
// and `WarningAmber`. The paths are the pinned modsy checkout's
// `@mui/icons-material` own; WarningAmber's two `<path>`s are one here, a
// nonzero fill drawing them the same. Sized by the caller (the stepper's
// `fontSize: 48`).

/** `@mui/icons-material/CheckCircleOutlined`: the overlay's "Done!". */
export const MuiCheckCircleOutlinedIcon = muiGlyph(
  "mui-check-circle-outlined",
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z",
  ALERT_GLYPH_BOX,
);

/** `@mui/icons-material/WarningAmber`: the failure view, retries left. */
export const MuiWarningAmberIcon = muiGlyph(
  "mui-warning-amber",
  "M12 5.99 19.53 19H4.47zM12 2 1 21h22zM13 16h-2v2h2zm0-6h-2v5h2z",
  ALERT_GLYPH_BOX,
);

/** `@mui/icons-material/ErrorOutlined`: the failure view, retries exhausted. */
export const MuiErrorOutlinedIcon = muiGlyph(
  "mui-error-outlined",
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m1 15h-2v-2h2zm0-4h-2V7h2z",
  ALERT_GLYPH_BOX,
);
